import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { YoutubeOutlined, FilePdfOutlined } from "@ant-design/icons";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterRentVideoManual from "../components/RegisterRentVideoManual";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";
import * as Common from "../commons/common";

type DataIndex = keyof Types.RentVideoManualDto;

function RentVedioManualList() {
    const alert = useAlert();

    const [rentVideoManuals, setRentVedioManualVedios] = useState<Array<Types.RentVideoManualDto>>();
    const [currentRentVedioManual, setcurrentRentVedioManual] = useState<Types.RentVideoManualDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteRentVedioManual, setOpenDeleteRentVedioManual] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.RentVideoManualDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.RentVideoManualDto> = [
        {
            align: "center",
            title: String.manualId,
            dataIndex: "rentVideoManualId",
            key: "rentVideoManualId",
            ...getColumnSearchProps("rentVideoManualId"),
        },
        {
            align: "center",
            title: String.manualName,
            dataIndex: "rentVideoManualName",
            key: "rentVideoManualName",
            ...getColumnSearchProps("rentVideoManualName"),
        },
        {
            align: "center",
            title: "동영상 주소",
            dataIndex: "rentVideoManualUri",
            key: "rentVideoManualUri",
            render: (_, { rentVideoManualUri }) => {
                return (
                    <a href={rentVideoManualUri} target="_blank" rel="noreferrer noopener">
                        <YoutubeOutlined /> {rentVideoManualUri}
                    </a>
                );
            },
        },
        {
            align: "center",
            title: "이미지 주소",
            dataIndex: "imageUrl",
            key: "imageUrl",
            render: (_, { imageUrl }) => {
                return (
                    <a href={imageUrl} target="_blank" rel="noreferrer noopener">
                        <FilePdfOutlined /> {imageUrl}
                    </a>
                );
            },
        },
    ];

    const requestAxiosGetRentVedioManuals = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_VIDEO_MANUAL_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetRentVedioManuals,
        error: errorGetRentVedioManuals,
        data: resultGetRentVedioManuals,
        execute: requestGetRentVedioManuals,
    } = useAsyncAxios(requestAxiosGetRentVedioManuals);

    useEffect(() => {
        if (resultGetRentVedioManuals === null) return;

        setRentVedioManualVedios(resultGetRentVedioManuals.rentVideoManuals);
    }, [resultGetRentVedioManuals]);

    useEffect(() => {
        if (errorGetRentVedioManuals === null) return;

        alert.setAlert(errorGetRentVedioManuals.response.data.code, "Error", errorGetRentVedioManuals.response.data.message);
    }, [errorGetRentVedioManuals]);

    const requestAxiosRemoveRentVedioManual = async (data: Types.RentVideoManualDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_VIDEO_MANUAL_URL + "/" + data.rentVideoManualId);
        return response.data;
    };

    const {
        loading: loadingRemoveRentVedioManual,
        error: errorRemoveRentVedioManual,
        data: resultRemoveRentVedioManual,
        execute: requestRemoveRentVedioManuals,
    } = useAsyncAxios(requestAxiosRemoveRentVedioManual);

    useEffect(() => {
        if (resultRemoveRentVedioManual === null) return;

        alert.setAlert(AlertType.SUCCESS, "매뉴얼 삭제 성공", "매뉴얼 삭제를 완료하였습니다.");

        requestGetRentVedioManuals();
    }, [resultRemoveRentVedioManual]);

    useEffect(() => {
        if (errorRemoveRentVedioManual === null) return;

        alert.setAlert(errorRemoveRentVedioManual.response.data.code, "Error", errorRemoveRentVedioManual.response.data.message);
    }, [errorGetRentVedioManuals]);

    useEffect(() => {
        requestGetRentVedioManuals();
    }, []);

    const onRow = (value: Types.RentVideoManualDto) => {
        setEditMode("none");
        setcurrentRentVedioManual(value);
    };

    const handleDeleteRentVedioManual = () => {
        if (currentRentVedioManual !== undefined) setOpenDeleteRentVedioManual(true);
    };

    const handleDeleteRentVedioManualOk = () => {
        setOpenDeleteRentVedioManual(false);
        requestRemoveRentVedioManuals(currentRentVedioManual);
    };

    const handleDeleteRentVedioManualCancel = () => {
        setOpenDeleteRentVedioManual(false);
    };

    const onRegisterRentVedioManual = () => {
        requestGetRentVedioManuals();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>동영상 매뉴얼</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteRentVedioManual()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="rentVideoManualId" dataSource={rentVideoManuals} onRow={onRow} />
            {editMode === "add" && <RegisterRentVideoManual editMode={editMode} onRegisterRentVedioManual={onRegisterRentVedioManual} />}
            {editMode !== "add" && currentRentVedioManual !== undefined && (
                <RegisterRentVideoManual
                    editMode={editMode}
                    rentVideoManual={currentRentVedioManual}
                    onRegisterRentVedioManual={onRegisterRentVedioManual}
                />
            )}

            {openDeleteRentVedioManual && (
                <Modal open={openDeleteRentVedioManual} onOk={handleDeleteRentVedioManualOk} onCancel={handleDeleteRentVedioManualCancel}>
                    {currentRentVedioManual?.rentVideoManualName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default RentVedioManualList;
